import type { Material, Mesh, Object3D } from 'three'
import { ToonMaterial } from './toonMaterial'

/**
 * Whether a draw group throws a shadow — the renderer's switch **and** the
 * material's pass, which is how Unity decides it.
 *
 * 📐 Two independent switches, both read from the export:
 *
 *  - `MeshRenderer.m_CastShadows` on the node (`ManifestNode.castShadows`).
 *    Off on the bus's `IceCountText`, the tunnel cover's stencil `Cube` and the
 *    scene's swept `SplineMesh (1)`; On on everything else the port draws.
 *  - The material's `SHADOWCASTER` pass (`ManifestMaterial.castShadows`),
 *    which six `.mat` files disable: `BusShadows` — submesh 1 of every bus body
 *    piece —, `BusStationGlass` — submesh 1 of the stop and its doors —,
 *    `Ice 1`, `Node`, `TangentLine`, `BusStation04_1Mat`. A renderer set to On
 *    still throws nothing from those groups.
 *
 * three has the first switch (`Object3D.castShadow`) and not the second: its
 * shadow pass walks every draw group of a mesh with a material array and draws
 * each one whose material is `visible`. So a mesh that mixes casting and
 * non-casting groups — a bus body piece is `[Bus_Red, BusShadows]` — gets the
 * `onBeforeShadow` / `onAfterShadow` hooks, which mask the depth material's
 * writes for the group that has no pass. The hooks read the object's *current*
 * material list, not the one at build time, because a bus swaps its materials
 * for the tunnel-cut and hidden twins and those carry the same flag.
 *
 * Masking rather than skipping: the pass calls `renderBufferDirect` straight
 * after the hook, and `state.setMaterial` applies `colorWrite` / `depthWrite`
 * per draw, so a group drawn with both off leaves the shadow map untouched.
 * The depth material is shared across every object in the pass, hence the
 * restore in `onAfterShadow`.
 */
export function materialCastsShadow(material: Material): boolean {
  return material instanceof ToonMaterial ? material.castShadows : true
}

/** Set `mesh.castShadow` from the node's renderer flag and its materials' passes. */
export function applyShadowCasting(mesh: Mesh, rendererCasts: boolean): void {
  if (!rendererCasts) {
    mesh.castShadow = false
    return
  }
  const material = mesh.material
  if (!Array.isArray(material)) {
    mesh.castShadow = materialCastsShadow(material)
    return
  }
  mesh.castShadow = material.some(materialCastsShadow)
  mesh.onBeforeShadow = maskNonCastingGroup
  mesh.onAfterShadow = restoreDepthWrites
}

/**
 * Carry one mesh's casting decision onto a copy of it (the seam twin) — the
 * switch, the group hooks, and the road-cut depth material
 * (`roadCutShadow.ts`), which is the third per-bus decision a copy has to
 * inherit (§8: ask every one of them again for the twin).
 */
export function copyShadowCasting(from: Mesh, to: Mesh): void {
  to.castShadow = from.castShadow
  to.onBeforeShadow = from.onBeforeShadow
  to.onAfterShadow = from.onAfterShadow
  to.customDepthMaterial = from.customDepthMaterial
}

type ShadowHook = Object3D['onBeforeShadow']

// The typings say (renderer, scene, camera, shadowCamera, geometry,
// depthMaterial, Group); what `WebGLShadowMap.renderObject` actually passes is
// (renderer, **object**, camera, shadowCamera, geometry, depthMaterial,
// **geometry group** or null) — see `three/src/renderers/webgl/WebGLShadowMap.js`.
const maskNonCastingGroup: ShadowHook = (_renderer, object, _camera, _shadowCamera, _geometry, depthMaterial, group) => {
  const drawGroup = group as unknown as { materialIndex: number } | null
  if (!drawGroup) return
  const material = (object as unknown as Mesh).material
  const groupMaterial = Array.isArray(material) ? material[drawGroup.materialIndex] : material
  if (groupMaterial && !materialCastsShadow(groupMaterial)) {
    depthMaterial.colorWrite = false
    depthMaterial.depthWrite = false
  }
}

const restoreDepthWrites: ShadowHook = (_renderer, _object, _camera, _shadowCamera, _geometry, depthMaterial) => {
  depthMaterial.colorWrite = true
  depthMaterial.depthWrite = true
}
